import { nanoid } from "nanoid";
import {
  useState,
  useRef,
  useCallback,
  forwardRef,
  useImperativeHandle,
} from "react";
import { Col, Row, Input, Button } from "@neumetric/blueprint-lib/components";
import { useSelector } from "react-redux";
import { NMReportPage } from "@neumetric/blueprint-lib/pages";
import { SearchVariable } from "../../Common";
import {
  getCurrentUser,
  getCurrentModule,
} from "@neumetric/blueprint-lib/store";
import { DocumentTabs } from "./DocumentTabs";

const HeaderData = {
  reportId: "[[REPORT_ID]]",
  reportDate: "[[REPORT_DATE]]",
  status: <span>[[STATUS]]</span>,
  reportTitle: "[[REPORT_TITLE]]",
  reportFileName: "[[FILE_NAME]]",
  orgName: "[[ORG_NAME]]",
  department: "[[DEPARTMENT]]",
  itemsOrder: [
    "reportId",
    "reportDate",
    "status",
    "reportTitle",
    "reportFileName",
  ],
};

const FooterData = {
  showPageNo: true,
  copyright: (
    <span>
      Controlled Master of this document exists within the internal network of
      Luciance. Printed copies, standalone versions in PDF or other file formats
      & copies outside this network are considered
      <span className="ml-2 mr-2 text-caution fw-600">"Uncontrolled"</span> &
      may not be up-to-date.
    </span>
  ),
  infosec: (
    <span>
      Uncontrolled copies of this document should not be distributed freely.
      Contact [[ORG_BRAND_NAME]] at [[INFOSEC_EMAIL]] to obtain the latest
      version of this document.
    </span>
  ),
  showOrgLogo: true,
  showModuleLogo: true,
};

export const Soc2Type2Report = forwardRef((props, ref) => {
  const { initialMode, initialData } = props;
  const _pageData = useRef(initialData ? [...initialData.content] : []);
  const _pageEditors = useRef([]);
  const _container = useRef(null);
  const _docTabRef = useRef(null);
  const currentModule = useSelector(getCurrentModule);
  const currentUser = useSelector(getCurrentUser);
  const [state, setState] = useState({
    pages: initialData ? initialData.content.map(() => nanoid(8)) : [nanoid(8)],
    mode: initialMode || "edit",
    showAddVariable: false,
  });

  const handleAddPage = useCallback(
    (pageNo) => {
      _pageData.current.splice(pageNo + 1, 0, "");
      const pages = [...state.pages];
      pages.splice(pageNo + 1, 0, nanoid(8));
      setState((_) => ({ ..._, pages: pages }));
    },
    [state.pages],
  );

  const handleRemovePage = useCallback(
    (pageNo) => {
      _pageData.current.splice(pageNo, 1);
      const pages = state.pages.filter((_, index) => index !== pageNo);
      setState((_) => ({ ..._, pages: pages }));
    },
    [state.pages],
  );

  const handlePageChange = (pageNo, value) => {
    _pageData.current[pageNo] = value;
  };

  const handleInstanceReady = (pageNo, event) => {
    _pageEditors.current[pageNo] = event.editor;
  };

  const getTableOfContentsId = (title, pageNo, index) => {
    const slug = (title || "")
      .toLowerCase()
      .trim()
      .replace(/[^a-z0-9]+/g, "-")
      .replace(/^-+|-+$/g, "");

    return `toc-${slug || "section"}-${pageNo}-${index + 1}`;
  };
  const getPageSanitizedContent = (iPageNo) => {
    let content = _pageData.current[iPageNo] || "";
    return content.replace(/&nbsp;/gi, " ").replace(/\u00A0/g, " ");
  };

  const generateTableOfContents = (tableOfContents) => {
    const tocEntriesPerPage = 34;
    if (tableOfContents.length > 0) {
      const handleTocLinkClick = (event, targetId) => {
        event.preventDefault();
        const target = _container.current?.querySelector(`#${targetId}`);

        if (target) {
          target.scrollIntoView({
            behavior: "smooth",
            block: "start",
          });
        }
      };

      const createTocWrapper = (items) => {
        const tocWrapper = document.createElement("div");
        items.forEach((item) => {
          const row = document.createElement("div");
          const isFirstLevel = Number(item.level) === 0;
          const fontSize = isFirstLevel ? "fs-16" : "fs-14";
          const fontWeight = isFirstLevel ? "fw-700" : "fw-500";
          row.classList.add(
            "d-flex",
            "flex-row",
            "align-center",
            "justify-between",
            "line-auto",
            `pl-[${item.level * 2}rem]`,
            "mt-3",
            "mb-3",
          );
          const titleLink = document.createElement("a");
          titleLink.href = `#${item.id}`;
          titleLink.classList.add(fontSize, "text-action", fontWeight);
          titleLink.style.textDecoration = "none";
          titleLink.textContent = item.title;
          titleLink.addEventListener("click", (event) =>
            handleTocLinkClick(event, item.id),
          );

          const pageLink = document.createElement("a");
          pageLink.href = `#${item.pageId}`;
          pageLink.classList.add(fontSize, "text-action", fontWeight);
          pageLink.style.textDecoration = "none";
          pageLink.textContent = item.pageNo;
          pageLink.addEventListener("click", (event) =>
            handleTocLinkClick(event, item.pageId),
          );

          row.appendChild(titleLink);
          row.appendChild(pageLink);
          tocWrapper.appendChild(row);
        });

        return tocWrapper;
      };

      const tocChunks = [];
      for (
        let index = 0;
        index < tableOfContents.length;
        index += tocEntriesPerPage
      ) {
        tocChunks.push(tableOfContents.slice(index, index + tocEntriesPerPage));
      }

      const placeholderNodes = [];
      const walker = document.createTreeWalker(
        _container.current,
        NodeFilter.SHOW_TEXT,
        null,
        false,
      );

      let node;
      while ((node = walker.nextNode())) {
        if (node.nodeValue.includes("[[TableOfContents]]")) {
          placeholderNodes.push(node);
        }
      }

      placeholderNodes.forEach((placeholderNode, index) => {
        const parent = placeholderNode.parentNode;
        const newNode = document.createElement("span");
        const chunk = tocChunks[index];
        if (chunk?.length) {
          newNode.appendChild(createTocWrapper(chunk));
        }
        parent.replaceChild(newNode, placeholderNode);
      });
    }
  };

  const getTableOfContentsItems = () => {
    setState((_) => ({ ..._, mode: "view" }));
    return new Promise((resolve) => {
      setTimeout(() => {
        const allSectionTitles =
          _container.current.querySelectorAll(".nm-report-title");
        const tableOfContents = [];
        allSectionTitles.forEach((sectionTitle, index) => {
          const level = sectionTitle.getAttribute("level");
          const pageContainer = sectionTitle.closest("[data-pageno]");
          const title = sectionTitle.textContent?.trim();

          if (pageContainer) {
            const pageNo = pageContainer.getAttribute("data-pageno");
            const sectionId = getTableOfContentsId(title, pageNo, index);
            const pageId = `page-${pageNo}`;
            pageContainer.id = pageId;
            sectionTitle.id = sectionId;
            sectionTitle.setAttribute("data-toc-id", sectionId);
            tableOfContents.push({
              id: sectionId,
              element: sectionTitle,
              pageId,
              title,
              pageNo,
              level: level,
            });
          }
        });

        resolve(tableOfContents);
      }, 1000);
    });
  };

  const getPageContent = (pageNo) => {
    const pageHTML = getPageSanitizedContent(pageNo);
    return (
      <Col className="w-100 h-100 pos-relative">
        {pageNo === 0 && (
          <Col className="w-100 mt-40" align="center" justify="center">
            <Col
              className="mb-16 w-100-px h-60-px"
              align="center"
              justify="center"
            >
              <span className="fs-32 fw-600 text-heading">[[ORG_LOGO]]</span>
            </Col>
            <Col className="mb-16 fs-32 fw-600 text-heading">
              [[ORG_BRAND_NAME]]
            </Col>
            <Col className="fs-32 fw-600 text-heading mb-16">
              [[REPORT_TITLE]]
            </Col>
            <Col className="fs-32 fw-600 text-heading line-auto mb-20">
              [[CURRENT_YEAR]]
            </Col>
          </Col>
        )}
        <Col className="w-100 flex-auto">
          {state.mode === "view" ? (
            <div className={"ff-exo2 ck-container w-100 mb-8 h-100"}>
              <span dangerouslySetInnerHTML={{ __html: pageHTML }}></span>
            </div>
          ) : (
            <Input
              toolbarType="inline"
              classes={{
                input: "h-100 pl-0",
                root: "w-100 h-100",
              }}
              attribute={`page_${pageNo}`}
              typeRepresentation="HTML"
              value={pageHTML}
              config={{ allowedContent: true }}
              onChange={handlePageChange.bind(null, pageNo)}
              onInstanceReady={handleInstanceReady.bind(null, pageNo)}
            />
          )}
        </Col>
        {state.mode === "edit" && (
          <Row className="pos-absolute " style={{ bottom: 8, left: 0 }}>
            <Button
              label="Add Page"
              classes={{ button: "mr-10" }}
              onClick={() => handleAddPage(pageNo)}
            />
            {pageNo > 0 && (
              <Button
                label="Remove This Page"
                classes={{ button: "mr-10" }}
                onClick={() => handleRemovePage(pageNo)}
              />
            )}
            <Button
              label="Add Variable"
              classes={{ button: "mr-10" }}
              onClick={() => handleAddVariable(true, pageNo)}
            />
          </Row>
        )}
      </Col>
    );
  };

  const handleAddVariable = (show, pageNo) => {
    setState((_) => ({ ..._, showAddVariable: show, variablePageNo: pageNo }));
  };

  const handleCopyVariable = (variable) => {
    const editor = _pageEditors.current[state.variablePageNo];
    if (editor) {
      editor.insertText(variable.name);
    }
    handleAddVariable(false, null);
  };

  const handleTocSelect = useCallback(
    (node) => {
      const container = _container.current;
      var element = null;
      if (state.mode === "view") {
        element = document.getElementById(node.id);
      } else if (state.mode === "edit") {
        element = document.getElementById(`#page-${node.pageNo}`);
      }

      if (!container || !element) return;

      const containerRect = container.getBoundingClientRect();
      const elementRect = element.getBoundingClientRect();

      const scrollTop =
        container.scrollTop + (elementRect.top - containerRect.top);

      container.scrollTo({
        top: scrollTop,
        behavior: "smooth",
      });
    },
    [state.mode],
  );

  const handleGenerateTableOfContents = () => {
    getTableOfContentsItems().then((tocContents) => {
      _docTabRef.current.setItems(tocContents);
      generateTableOfContents(tocContents);
    });
  };

  useImperativeHandle(ref, () => ({
    generateTableOfContents: handleGenerateTableOfContents,
    getData: () => {
      return _pageData.current;
    },
    setMode: (mode) => setState((_) => ({ ..._, mode })),
  }));

  return (
    <Row className="w-100 h-100 overflow-hidden" align="start">
      <DocumentTabs ref={_docTabRef} items={[]} onSelect={handleTocSelect} />
      <Col className="flex-auto h-100 overflow-auto pa-20" ref={_container}>
        {state.pages.map((pageKey, index) => {
          const firstPage = index === 0;
          return (
            <NMReportPage
              orgLogo={currentUser.org.logo_url}
              moduleLogo={currentModule.logo}
              key={pageKey}
              pageNo={index + 1}
              totalPages={state.pages.length}
              header={firstPage ? undefined : HeaderData}
              footer={FooterData}
            >
              {getPageContent(index)}
            </NMReportPage>
          );
        })}
        {state.showAddVariable && (
          <SearchVariable
            onClose={() => handleAddVariable(false, null)}
            onCopy={handleCopyVariable}
          />
        )}
      </Col>
    </Row>
  );
});
